(ns common.schema.entities.fund-commitment.core
  (:require [common.roles :as roles]
            [common.schema.entities.fund-commitment :as fund-commitment]
            [common.schema.entities.fundraising-opportunity.permissions :as froppo-permissions]
            [common.schema.entities.permissions :as entity-permissions]))

;; Verbatim copy of common.schema.entity-types's existing fund-commitment entry
;; (entity_types.cljc around line 802-838) - required here, not there, to avoid
;; a circular namespace dependency (entity_types.cljc requires this registry).
;; Keep in sync if the original ever changes.
;;
;; One deliberate deviation: the original :entity/read-permissions-check-fn
;; recursively calls entity_types.cljc's own generic read-permissions? on a
;; related fundraising-opportunity entity (to grant access to a commitment
;; via the froppo it originated from). That generic dispatcher isn't
;; reachable here without re-introducing the cycle, so its two effects -
;; admin bypass and the tenant-ownership check - are reproduced inline below
;; via froppo-read-permissions?, then froppo's own entity-specific check
;; (common.schema.entities.fundraising-opportunity.permissions/read-permissions?,
;; already the same fn plugged into fundraising-opportunity's own core.cljc)
;; is called directly. Fundraising-opportunity does not set
;; :entity/apply-read-permissions-to-admin?, so the admin bypass here matches
;; today's behavior exactly.
(defn- froppo-read-permissions?
  [{:keys [role tenant] :as ctx} fo]
  (and (= (:db/id tenant) (:db/id (:common/tenant fo)))
       (or (roles/admin? role)
           (froppo-permissions/read-permissions? (assoc ctx :entity fo)))))

(defn- read-permissions-check-fn
  [{:keys [account-current-permissions entity] :as ctx}]
  (let [account-modules (:account-modules account-current-permissions)]
    (or
     (contains? account-modules :module/fundraising)
     (contains? account-modules :module/portfolio)
     (->> entity
          :fundraising-opportunity/_fund-commitment
          (remove :common/disabled?)
          (some (partial froppo-read-permissions? ctx)))
     (contains? (:account-access-to-funds account-current-permissions)
                (->> entity
                     :fund-commitment/fund
                     :db/id))
     (let [originated-froppo (->> entity
                                  :fundraising-opportunity/_fund-commitment
                                  (remove :common/disabled?)
                                  (first))]
       (contains? (:account-access-to-pipelines account-current-permissions)
                  (->> originated-froppo
                       :fundraising-opportunity/pipeline
                       :db/id))))))

(def entity-type-definition
  {:entity-type                  :entity.type/fund-commitment
   :schema                       fund-commitment/schema
   :name                         {:en "Fund commitment"}
   :plural                       {:en "Commitments"}
   :icon                         :fa-money-check-edit-alt
   :create-icon                  :fa-money-check-edit-alt
   :read-permissions-check-fn    read-permissions-check-fn
   :disable-permissions-check-fn entity-permissions/disable-when-no-write-permission
   ;; Explicitly false: fund-commitment's dedicated OpenSearch index already
   ;; exists and is populated via the legacy common.opensearch.entity-indices
   ;; mechanism, not the newer :entity/search-index?-driven
   ;; backend.search.dedicated.indexes/init! path - this key would otherwise
   ;; default to true just because :search is present below.
   :search-index?                false
   ;; :result/title-attr uses the real, schema-calculated
   ;; :fund-commitment/name (not :common/display-name) - a kanban card's
   ;; title/body only ever reads flat.<alias> paths
   ;; (ui.view.kanban-opensearch.event/compile-card-field), and
   ;; :common/display-name is never flattened (only materialized under
   ;; original/system) - using it as a listing's :result/title-attr renders
   ;; a working grid column but silently produces zero kanban cards (see
   ;; ai/mistakes.md). :fund-commitment/name's composite formula was
   ;; trimmed to just investor+fund (see NOTE-1 in
   ;; common.schema.entities.fund-commitment) specifically so it can be
   ;; used here directly, with no custom :display-name-fn needed.
   ;; :list/default-columns previously ended with
   ;; :fund-commitment/cur-remaining-commitment/-cur-called-amount/
   ;; -cur-distributed-amount, all three of which this tenant has disabled
   ;; via tenant/field-configurations (:field-not-in-use) - fine for the
   ;; grid (its columns are individually droppable), fatal for kanban:
   ;; listing-definition/card-config always uses the *last* default column
   ;; as the card's footer field, and header/footer fields are never
   ;; filtered against accessible fields the way body fields are
   ;; (frontend.ux-overhaul.listing.generic.view/apply-card-overrides only
   ;; sanitizes :body-fields) - so every kanban-column request included an
   ;; inaccessible field and backend.domain.search.request-validation threw
   ;; "No access to requested field" for 100% of them, with zero cards ever
   ;; rendering and no visible client-side error (see ai/mistakes.md for the
   ;; full incident-log-based diagnosis). Trimmed to only
   ;; tenant-config-independent-safe fields: :fund-commitment/name and
   ;; :fund-commitment/fund are structural (never individually disabled in
   ;; practice), :cur-committed-amount happens to be enabled for this
   ;; tenant. A tenant disabling *this* field too would reproduce the same
   ;; failure - the underlying header/footer-field accessibility gap in the
   ;; generic kanban card config is not fixed here, only worked around for
   ;; this entity's own defaults.
   :search                       {:result/title-attr :fund-commitment/name
                                  :list/default-columns [:fund-commitment/name
                                                         :fund-commitment/fund
                                                         :fund-commitment/cur-committed-amount]
                                  :views {:grid {:po-default-field-paths [[:fund-commitment/name]
                                                                          [:fund-commitment/fund]
                                                                          [:fund-commitment/cur-committed-amount]]
                                                 :mandatory-field-paths [[:fund-commitment/name]]}}}})
